import { memo, useCallback, type MouseEvent } from 'react';
import { toast } from 'react-toastify';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface Props {
    value?: string | null;
    /** What the toast calls this, e.g. "User ID". */
    label?: string;
}

/**
 * A table cell whose text can be copied without being selected by hand.
 *
 * Identifiers — Firebase UIDs, installation IDs, result IDs — are never read, only pasted into a
 * console or a support thread, and hand-selecting 28 characters out of a grid row is fiddly and
 * easy to get wrong. The icon only appears on hover so it costs no width at rest.
 */
function CopyableCell({ value, label = 'Value' }: Props) {
    const handleCopy = useCallback(
        (event: MouseEvent) => {
            // The row itself is often clickable.
            event.stopPropagation();
            if (!value) return;
            navigator.clipboard.writeText(value);
            toast.info(`${label} copied`);
        },
        [value, label],
    );

    if (!value) {
        return (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                —
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                width: '100%',
                minWidth: 0,
                '& .copy-affordance': { opacity: 0, transition: 'opacity 120ms' },
                '&:hover .copy-affordance': { opacity: 1 },
            }}
        >
            <Typography variant="body2" noWrap title={value}>
                {value}
            </Typography>
            <Tooltip title={`Copy ${label.toLowerCase()}`}>
                <IconButton
                    className="copy-affordance"
                    size="small"
                    aria-label={`Copy ${label.toLowerCase()}`}
                    onClick={handleCopy}
                    sx={{ p: 0.25 }}
                >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

export default memo(CopyableCell);
